import React, { useState } from 'react';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { produce } from 'immer';

import { FeaturePatternTrussContainerSlopeSettings } from '@paragon/contract-library';
import { AngleField } from '@paragon/material';

import { FeaturePatternContainerPatchOrUpdateFn } from '../../../store/actions/layout/featurePatternContainerActions';
import { LayoutLinePickerField } from '../../interface/subpanels/insertions/truss-envelope/LayoutLinePickerField';

interface Props {
  readonly disabled?: boolean;

  readonly value: FeaturePatternTrussContainerSlopeSettings | null;
  readonly onChange: (changes: FeaturePatternContainerPatchOrUpdateFn) => void;
}

function NoSlope(props: { onEscapingNullSlope(): void; readonly disabled?: boolean }) {
  return (
    <Stack direction="row" sx={{ alignItems: 'baseline' }}>
      <Typography>No slope.</Typography>

      <Button
        variant="outlined"
        sx={{ marginLeft: 'auto' }}
        onClick={props.onEscapingNullSlope}
        disabled={props.disabled}
      >
        Make Sloped
      </Button>
    </Stack>
  );
}

export function FeaturePatternContainerSlopeControls({ disabled, value, onChange }: Props) {
  const [pendingTopSlope, setPendingTopSlope] = useState<number | null>(null);
  const [pendingBottomSlope, setPendingBottomSlope] = useState<number | null>(null);

  const escapingNullSlope = pendingTopSlope != null || pendingBottomSlope != null;

  if (value != null && escapingNullSlope) {
    setPendingTopSlope(null);
    setPendingBottomSlope(null);

    onChange(
      produce((container) => {
        if (pendingTopSlope != null) container.slopeSettings!.bottomSlopeInDegrees = pendingTopSlope;
        if (pendingBottomSlope != null) container.slopeSettings!.topSlopeInDegrees = pendingBottomSlope;
      }),
    );
  }

  if (value == null && !escapingNullSlope) {
    return (
      <NoSlope
        onEscapingNullSlope={() => {
          setPendingTopSlope(0);
          setPendingBottomSlope(0);
        }}
        disabled={disabled}
      />
    );
  }

  const topSlope = value?.topSlopeInDegrees ?? pendingTopSlope;
  const bottomSlope = value?.bottomSlopeInDegrees ?? pendingBottomSlope;

  return (
    <Grid container spacing={1}>
      <Grid size={12} sx={{ marginTop: 1 }}>
        <Stack spacing={2}>
          <LayoutLinePickerField
            label="Edge to Slope From"
            buttonText={value?.edgeToSlopeFrom ? 'Pick Again' : 'Pick'}
            target="feature-pattern-container-slope-base-edge"
            value={value?.edgeToSlopeFrom ? '<container edge chosen>' : null}
            disabled={disabled}
          />

          <Box sx={{ marginLeft: 'auto' }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setPendingTopSlope(null);
                setPendingBottomSlope(null);

                onChange(
                  produce((container) => {
                    container.slopeSettings = null;
                  }),
                );
              }}
              disabled={disabled}
            >
              Remove Slope
            </Button>
          </Box>
        </Stack>
      </Grid>

      <Grid size={6}>
        <AngleField
          label="Bottom Slope"
          value={bottomSlope!}
          onSubmit={(updatedBottomSlope) => {
            if (escapingNullSlope) {
              setPendingBottomSlope(updatedBottomSlope);
            } else {
              onChange(
                produce((container) => {
                  container.slopeSettings!.bottomSlopeInDegrees = updatedBottomSlope;
                }),
              );
            }
          }}
          disabled={disabled}
        />
      </Grid>

      <Grid size={6}>
        <AngleField
          label="Top Slope"
          value={topSlope!}
          onSubmit={(updatedTopSlope) => {
            if (escapingNullSlope) {
              setPendingTopSlope(updatedTopSlope);
            } else {
              onChange(
                produce((container) => {
                  container.slopeSettings!.topSlopeInDegrees = updatedTopSlope;
                }),
              );
            }
          }}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
}
